import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { motion } from "framer-motion";
import { FaBookmark, FaShare, FaExternalLinkAlt } from "react-icons/fa";
import { fetchAllNews } from "../../../utils/apiServices";
import { testApiIntegration, checkApiKeys } from "../../../utils/testApi";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = [
    "All",
    "Crypto",
    "Commodities",
    "Technology",
    "Trading",
    "Security",
  ];

  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check API keys in development mode
        if (process.env.NODE_ENV === "development") {
          checkApiKeys();
        }

        const newsData = await fetchAllNews();
        setPosts(newsData);

        // Test API integration in development mode
        if (process.env.NODE_ENV === "development") {
          testApiIntegration();
        }
      } catch (err) {
        setError("Failed to load news. Please try again later.");
        console.error("Error loading news:", err);
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, []);

  const filteredPosts =
    selectedCategory === "All"
      ? posts
      : posts.filter((post) => post.category === selectedCategory);

  const handleReadMore = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="main-section">
      {/* Hero Section */}
      <section className="section position-relative overflow-hidden">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center "
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="d-inline-block"
            >
              <div className="hero-badge rounded-5">
                <span className="badge-icon">🚀</span>
                <span className="badge-text">Live Updates</span>
              </div>
            </motion.div>
            <h2 className="hero-title fw-bold  mb-3 mt-4">
              Discover the Future of{" "}
              <span className="highlight">Crypto & Commodities</span>
            </h2>
            <p className="lead text-muted mb-5">
              Real-time insights, market analysis, and the latest trends from
              CryptoPanic and Marketaux
            </p>

            <div className="justify-content-center align-items-center d-flex gap-5">
              {[
                { number: posts.length + "+", label: "Live Articles" },
                { number: "50K+", label: "Active Readers" },
                { number: "2", label: "News Sources" },
              ].map((stat, index) => (
                <motion.div className="stat-item" key={index}>
                  <h3 className="h2 fw-bold mb-1 text-primary">
                    {stat.number}
                  </h3>
                  <p className="text-muted mb-0 small">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Category Filter */}
      <motion.section
        className="category-filter py-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Container>
          <div className="d-flex justify-content-center gap-2 flex-wrap">
            {categories.map((category) => (
              <motion.button
                key={category}
                className={`btn ${
                  selectedCategory === category
                    ? "btn-primary"
                    : "btn-outline-primary"
                } btn-sm`}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </Container>
      </motion.section>

      {/* Featured Posts */}
      <motion.section
        className="featured-posts py-5"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Container>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="text-primary mb-0">
              {selectedCategory === "All"
                ? "Latest News"
                : `${selectedCategory} News`}
            </h2>
            {loading && (
              <div className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                <small className="text-muted">Loading...</small>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="warning" className="mb-4">
              <Alert.Heading>⚠️ Unable to Load News</Alert.Heading>
              <p>{error}</p>
              <hr />
              <p className="mb-0">
                Please check your API keys and internet connection. Using
                fallback data.
              </p>
            </Alert>
          )}

          {loading ? (
            <Row className="g-4">
              {[1, 2, 3, 4].map((index) => (
                <Col md={6} key={index}>
                  <Card className="h-100">
                    <div className="card-image-container overflow-hidden">
                      <div
                        className="placeholder-image bg-light"
                        style={{ height: "200px" }}
                      >
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <Spinner animation="border" />
                        </div>
                      </div>
                    </div>
                    <Card.Body>
                      <div className="placeholder-text">
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: "20px", width: "60%" }}
                        ></div>
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: "16px", width: "100%" }}
                        ></div>
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: "16px", width: "80%" }}
                        ></div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : filteredPosts.length > 0 ? (
            <Row className="g-4">
              {filteredPosts.map((post) => (
                <Col md={6} key={post.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Card className="hover-card h-100">
                      <div className="card-image-container overflow-hidden">
                        <motion.img
                          className="card-img-top"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                          src={post.image}
                          alt={post.title}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/400x250?text=News+Image";
                          }}
                        />
                        <div className="card-overlay">
                          <Badge bg="primary" className="category-badge">
                            {post.category}
                          </Badge>
                          <div className="post-meta">
                            <span className="read-time">{post.readTime}</span>
                            <span className="likes">{post.likes} likes</span>
                          </div>
                        </div>
                      </div>
                      <Card.Body>
                        <div className="author-info d-flex align-items-center mb-3">
                          <img
                            src={post.author.avatar}
                            alt={post.author.name}
                            className="author-avatar"
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/40x40?text=NA";
                            }}
                          />
                          <div className="ms-3">
                            <h6 className="mb-0">{post.author.name}</h6>
                            <small className="text-muted">
                              {post.author.role} • {post.source}
                            </small>
                          </div>
                        </div>
                        <Card.Title className="mt-2">{post.title}</Card.Title>
                        <Card.Text>{post.excerpt}</Card.Text>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">{post.date}</small>
                          <div className="post-actions d-flex gap-2">
                            <motion.button
                              className="btn btn-icon"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <FaBookmark />
                            </motion.button>
                            <motion.button
                              className="btn btn-icon"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <FaShare />
                            </motion.button>
                            <motion.button
                              className="btn btn-primary btn-md fw-bold px-4 flex-shrink-0"
                              whileHover={{ scale: 1.05 }}
                              onClick={() => handleReadMore(post.url)}
                            >
                              <FaExternalLinkAlt className="me-2" />
                              Read More
                            </motion.button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-5">
              <h4 className="text-muted">
                No news available for this category
              </h4>
              <p className="text-muted">
                Try selecting a different category or check back later.
              </p>
            </div>
          )}
        </Container>
      </motion.section>

      {/* Newsletter */}
      <motion.section
        className="newsletter py-5"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      ></motion.section>
    </div>
  );
};

export default Blog;
